import { useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiAvatar,
  MiCardList,
  MiIcon,
  MiIconButton,
  MiMenu,
} from "@miplatform/components";
import {
  ATTEMPT_DERIVED_STATUS_COLORS_MAP,
  ATTEMPT_DERIVED_STATUS_LABEL,
  ATTEMPT_STATUSES,
  DEFAULT_COMMUNITY_COLORS,
  ICONS_WEB,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import {
  getDateWithAddedTime,
  getDurationInHours,
  getFormattedDuration,
  getInitials,
  getRootUrl,
  isEmpty,
  toHour,
} from "@miplatform/utils";
import * as mdi from "@mdi/js";
import { Box, Collapse, Typography, useTheme } from "@mui/material";
import { TaskCardMenu } from "../TaskCard/TaskCardMenu/TaskCardMenu";
import { useUserDetailsViewModel } from "../UserDetails/useUserDetailsViewModel";

const communityColorObj = {};

const getColorByCommunity = (community, defaultColor) => {
  const communityColorArr = DEFAULT_COMMUNITY_COLORS;
  if (communityColorArr && community) {
    if (communityColorObj[community]) {
      return communityColorObj[community];
    }
    const colorIndex =
      community.split("").reduce((a, i) => a + i.charCodeAt(0), 0) %
      communityColorArr.length;
    communityColorObj[community] = communityColorArr[colorIndex];
    return communityColorArr[colorIndex];
  }
  return defaultColor;
};

const AttemptsList = ({
  appointments,
  onAttemptUpdated,
  handleOpenCloseoutDrawer,
}) => {
  const { classes } = useStyles();
  const theme = useTheme();

  const renderAttemptDuration = (value) => {
    const { exactExtendedTime = 0 } = value;
    const duration = getDurationInHours(
      value.displayStartTime,
      value.displayEndTime
    );
    const isInProgress = value.attemptStatus === ATTEMPT_STATUSES.STARTED;
    const diffInDuration = value.endDate
      ? getDurationInHours(value.startDate, value.endDate) -
        getDurationInHours(value.scheduledStartDate, value.scheduledEndDate)
      : 0;

    return (
      <Box>
        <Typography
          variant="caption"
          data-testid="actual-timer"
          className={classes.timeText}
        >
          {getFormattedDuration(duration + exactExtendedTime)}
        </Typography>
        <Typography
          variant="caption"
          data-testid="diff-total-time"
          className={classes.diffTotalTimeText}
          color={
            diffInDuration > 0 || exactExtendedTime > 0
              ? theme.palette.red.main
              : theme.palette.green.main
          }
        >
          {isInProgress
            ? ` (${exactExtendedTime > 0 ? "+" : "-"}${getFormattedDuration(
                Math.abs(exactExtendedTime)
              )})`
            : null}
          {diffInDuration
            ? ` (${diffInDuration > 0 ? "+" : "-"}${getFormattedDuration(
                Math.abs(diffInDuration)
              )})`
            : null}
        </Typography>
      </Box>
    );
  };

  const sections = [
    {
      testId: "attempts-list-section",
      rowStyle: { height: "100%" },
      fieldset: [
        {
          label: "Time",
          field: "createdOn",
          flexSize: 1,
          renderValue: (value) => {
            const cardStartTime = getDateWithAddedTime(
              value.displayStartTime || value.scheduledStartDate,
              { h: value.delayedTime || 0 }
            );
            const cardEndTime = getDateWithAddedTime(value.displayEndTime, {
              h: (value.extendedTime || 0) + (value.delayedTime || 0),
            });
            return (
              <Typography variant="caption">
                {`${getFormattedDuration(
                  toHour(cardStartTime),
                  "hh:mm A"
                )} - ${getFormattedDuration(toHour(cardEndTime), "hh:mm A")}`}
              </Typography>
            );
          },
        },
        {
          label: "Drive Time",
          field: "driveStartTime",
          flexSize: 1,
          renderValue: (value) => (
            <Typography variant="caption">
              {value.driveStartTime
                ? `${getFormattedDuration(
                    toHour(value.driveStartTime),
                    "hh:mm A"
                  )} - ${getFormattedDuration(
                    toHour(value.driveEndTime || new Date()),
                    "hh:mm A"
                  )}`
                : "--"}
            </Typography>
          ),
        },
        {
          label: "Duration",
          field: "duration",
          flexSize: 1,
          minWidth: 100,
          renderValue: renderAttemptDuration,
        },
        {
          label: "Tasks",
          field: "issues",
          flexSize: 1.5,
          renderValue: (value) => {
            return (
              <Typography variant="caption" noWrap>
                {value.issues?.length > 0
                  ? value.issues?.map((issue) => issue.title).join(", ")
                  : "No Tasks"}
              </Typography>
            );
          },
        },
        {
          label: "Type",
          field: "attempt",
          flexSize: 0.5,
          minWidth: 50,
          renderValue: (value) => {
            return (
              <Typography
                variant="caption"
                style={{
                  backgroundColor: value.assignment?.order?.orderTemplateColor,
                  padding: theme.spacing(0.5, 1),
                  borderRadius: 4,
                  color: theme.palette.common.white,
                }}
              >
                {value.assignment?.order?.orderTemplateAbbreviation}
              </Typography>
            );
          },
        },
        {
          label: "Order Status",
          field: "attemptStatus",
          flexSize: 1,
          renderValue: (value) => {
            return (
              <Typography
                variant="caption"
                style={{
                  color:
                    ATTEMPT_DERIVED_STATUS_COLORS_MAP[value.derivedStatus]
                      ?.title,
                  fontWeight: theme.fontWeight.semiBold,
                  borderRadius: 4,
                }}
              >
                {ATTEMPT_DERIVED_STATUS_LABEL[value.derivedStatus]}
              </Typography>
            );
          },
        },
        {
          label: "Address",
          field: "addressLine1",
          flexSize: 1.5,
          renderValue: (value) => {
            return (
              <Typography
                variant="caption"
                style={{
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
                noWrap
                onClick={() => {
                  handleOpenCloseoutDrawer(value);
                }}
              >
                {`${value.home?.address?.addressLine1 || ""}${
                  value.home?.address?.addressLine2
                    ? ` ${value.home?.address?.addressLine2}`
                    : ""
                }, ${value.home?.address?.city || ""}`}
              </Typography>
            );
          },
        },
        {
          label: "Community",
          field: "community",
          flexSize: 1,
          minWidth: 120,
          renderCell: (value) => {
            return (
              <Box
                style={{
                  display: "flex",
                  flexDirection: "column",
                  backgroundColor: getColorByCommunity(
                    value.home?.communityName,
                    theme.palette.bluegrey.light
                  ),
                  borderRadius: 8,
                  padding: theme.spacing(0.5, 1),
                  margin: theme.spacing(1, 0),
                }}
              >
                <Typography variant="caption" color="grey.main">
                  Community
                </Typography>
                <Typography variant="caption" noWrap>
                  {value.home?.communityName || "- -"}
                </Typography>
              </Box>
            );
          },
        },
        {
          label: "Builder",
          field: "builderName",
          flexSize: 0.75,
          minWidth: 100,
          renderValue: (value) => {
            return (
              <Typography variant="caption" noWrap>
                {value.home?.builderName || "- -"}
              </Typography>
            );
          },
        },
        {
          label: "Dispatcher",
          field: "dispatcherName",
          flexSize: 0.75,
          minWidth: 100,
          renderValue: (value) => {
            return (
              <Typography variant="caption" noWrap>
                {value.dispatcherName || "- -"}
              </Typography>
            );
          },
        },
        {
          label: "Attempt",
          field: "attemptNumber",
          flexSize: 0.5,
          minWidth: 50,
          renderValue: (value) => {
            return (
              <Typography variant="caption" noWrap>
                {value.attemptNumber}
              </Typography>
            );
          },
        },
        {
          label: "Notes",
          field: "orderNotes",
          flexSize: 1.5,
          renderValue: (value) => {
            return (
              <Typography variant="caption" wrap maxWidth={200}>
                {value.orderNotes || "- -"}
              </Typography>
            );
          },
        },
      ],
    },
  ];

  const renderItemActions = (item) => {
    return (
      <TaskCardMenu
        isCalendar={false}
        handleOpenCloseoutDrawer={handleOpenCloseoutDrawer}
        onAttemptUpdated={onAttemptUpdated}
        attempt={item}
        isInBucket={false}
      />
    );
  };

  return (
    <Box className={classes.attemptsList}>
      {appointments.map((item, i) => {
        const colors =
          ATTEMPT_DERIVED_STATUS_COLORS_MAP[item.derivedStatus] || {};

        return (
          <MiCardList
            key={`${item.id}-${i}`}
            item={item}
            sections={sections}
            renderItemActions={renderItemActions}
            leftCardProps={{ fieldset: [] }}
            containerStyles={{
              padding: theme.spacing(0, 2),
              margin: theme.spacing(0.5, 1),
              backgroundColor: colors.background || theme.palette.common.white,
              borderColor: theme.palette.grey.accent,
              borderWidth: 1,
              overflow: "hidden",
            }}
            showNulls
          />
        );
      })}
    </Box>
  );
};

const UserAccordion = ({
  user,
  onAttemptUpdated,
  handleOpenCloseoutDrawer,
}) => {
  const { classes } = useStyles();
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const theme = useTheme();
  const { navigate } = useRouter();
  const { activityStatus, name } = user;
  const { activityStatuses } = useUserDetailsViewModel({ user });
  const { icon: activityIcon, color: activityIconColor } =
    activityStatuses.find((status) => status.value === activityStatus) || {};

  return (
    <Box>
      <Box
        className={classes.userContainer}
        style={{ borderLeft: `8px solid ${activityIconColor}` }}
      >
        <Box style={theme.styles.ROW} justifyContent="space-between">
          <Box style={theme.styles.ROW} gap={1}>
            <MiIcon
              size={1}
              path={activityIcon || mdi[ICONS_WEB.CIRCLE_SLICE]}
              color={activityIconColor || theme.palette.grey.medium}
            />
            <MiAvatar
              className={classes.avatar}
              data-testid="user-initials"
              src={
                user?.profilePicture
                  ? `${getRootUrl()}/document-service/documents/${
                      user?.profilePicture.id
                    }`
                  : undefined
              }
            >
              {getInitials(name)}
            </MiAvatar>
            <Box display="flex" flexDirection="column">
              <Typography variant="body2" className={classes.userName}>
                {name}
              </Typography>
              <Box style={theme.styles.ROW} gap={3}>
                <Typography
                  variant="caption"
                  className={classes.activityStatus}
                >
                  {activityStatus}
                </Typography>
                <Typography
                  variant="caption"
                  className={classes.activityStatus}
                >
                  {user.appointments.length} Jobs
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box style={theme.styles.ROW} gap={1}>
            <MiIconButton
              onClick={() => setIsAccordionOpen(!isAccordionOpen)}
              disabled={user.appointments.length === 0}
            >
              <MiIcon
                size={1}
                path={isAccordionOpen ? mdi.mdiChevronUp : mdi.mdiChevronDown}
                color={
                  user.appointments.length
                    ? theme.palette.grey.main
                    : theme.palette.grey.darkAccent
                }
              />
            </MiIconButton>
            <MiMenu
              testID="user-details-menu"
              data={[
                {
                  label: "View More",
                  icon: mdi.mdiArrowRightThin,
                  action: () => {
                    navigate(`/settings/user-management/users/${user.id}`);
                  },
                },
                ...activityStatuses.map((status) => ({
                  ...status,
                  action: (_, item) => {
                    status.action(item);
                  },
                })),
              ]}
            />
          </Box>
        </Box>
      </Box>
      <Collapse in={isAccordionOpen}>
        <AttemptsList
          appointments={user.appointments}
          onAttemptUpdated={onAttemptUpdated}
          handleOpenCloseoutDrawer={handleOpenCloseoutDrawer}
        />
      </Collapse>
    </Box>
  );
};

const getUserData = (users, data) => {
  const userData = users.map((user) => {
    const userAttempts = data.filter((item) => item.assignedUserId === user.id);
    return {
      ...user,
      appointments: userAttempts || [],
    };
  });
  return userData;
};

export const ListView = ({
  data = [],
  users = [],
  onAttemptUpdated,
  handleOpenCloseoutDrawer,
  EmptyUser,
}) => {
  const { classes } = useStyles();
  const userData = getUserData(users, data);

  return (
    <Box className={classes.container}>
      <Box className={classes.mainContent}>
        {userData.map((item) => (
          <UserAccordion
            key={item.id}
            user={item}
            onAttemptUpdated={onAttemptUpdated}
            handleOpenCloseoutDrawer={handleOpenCloseoutDrawer}
          />
        ))}
        {isEmpty(userData) && <EmptyUser />}
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    backgroundColor: theme.palette.grey.lightAccent,
    borderTop: `1px solid ${theme.palette.grey.accent}`,
    display: "flex",
    flex: 1,
    overflow: "hidden",
  },
  mainContent: {
    flex: 1,
    width: "100%",
    overflowY: "auto",
    padding: theme.spacing(2),
    margin: theme.spacing(2),
    borderRadius: 4,
    backgroundColor: theme.palette.common.white,
  },
  userContainer: {
    padding: theme.spacing(2),
    margin: theme.spacing(1),
    marginBottom: 0,
    borderRadius: 4,
    backgroundColor: theme.palette.grey.light,
    boxShadow: theme.shadows[1],
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  avatar: {
    width: 32,
    height: 32,
    fontSize: theme.fontSize.medium,
  },
  activityStatus: {
    color: theme.palette.grey.medium,
  },
  userName: {
    fontWeight: theme.fontWeight.semiBold,
  },
  attemptsList: {
    display: "flex",
    flexDirection: "column",
  },
  timeText: {
    fontWeight: theme.fontWeight.bold,
    fontSize: theme.fontSize.medium,
  },
  diffTotalTimeText: {
    fontSize: theme.fontSize.medium,
  },
}));
